// Pure data tables for the runtime feature-flag registry — split out of
// featureFlags.js so every reader in that file becomes a cross-file IMPORT
// instead of a same-file sibling-export read.
//
// FA204 / frank trap (project_frank_drops_module_local_helpers): a
// same-file sibling-export/module-local read is unresolvable once the
// reading function is individually frank.toJSON'd for platform delivery;
// a cross-file ESM import resolves fine via frank's globalScope prototype-
// chain fallback (independently proven in this codebase — see the
// `__scope` construction recovered from the served platform payload:
// `const s = el?.scope; const g = el?.context?.globalScope; ... return s ??
// g ?? {}`). These tables are ~700 lines of hand-authored flag metadata —
// duplicating them per consuming function (the usual per-export inline-copy
// recipe) would be correct but turn every future flag edit into a 6+-way
// find-and-replace. Moving them here once, imported everywhere they're
// read, gets the same frank-safety with none of that maintenance cost.
//
// Nothing in this file is itself a DOMQL-registered function — it's pure
// data plus one pure derivation (`FEATURE_FLAGS`) and one pure index build
// (`FLAG_INDEX`), both computed ONCE at module-evaluation time (not inside
// an exported function body), so neither is subject to the trap at all.

// ─── User-facing runtime feature flags (the /flags page) ────────────────
//
// Unlike the migration constants above (module-level, flipped at build
// time by an engineer), these are RUNTIME toggles: the registry of every
// user-visible product feature, rendered at /flags where users switch
// features on/off. Core "basics" are locked always-on.
//
// Persistence v1 is per-device: overrides live in localStorage and are
// mirrored onto root state as `_featureFlags` so reactive `hide:`/CSS
// factories re-run on toggle. Server-side prefs sync (the
// homeDashboardPrefs `/core/prefs` route family) is the planned
// follow-up — keep the shape { [key]: boolean } so the same object can
// ride a prefs payload unchanged.
//
// Consumers gate surfaces with `featureEnabled(s.root, 'chat')` — core
// flags always return true, unknown keys default to ON (a missing
// registry entry must never dark-launch-hide a live feature).
// (2026-07-25 per Nika "build /flags page for all the feature flags that
// users can turn on and off … basics always on".)

export const FEATURE_FLAG_GROUPS = [
  {
    key: 'basics',
    label: 'flags.group.basics.label',
    description: 'flags.group.basics.descr'
  },
  {
    key: 'views',
    label: 'flags.group.views.label',
    description: 'flags.group.views.descr'
  },
  {
    key: 'modules',
    label: 'flags.group.modules.label',
    description: 'flags.group.modules.descr'
  },
  {
    key: 'ai',
    label: 'flags.group.ai.label',
    description: 'flags.group.ai.descr'
  },
  {
    key: 'apps',
    label: 'flags.group.apps.label',
    description: 'flags.group.apps.descr'
  }
]

export const RAW_FEATURE_FLAGS = [
  // ── Basics — core: locked ON, the switch renders as an "Always on" pill.
  {
    key: 'shell',
    group: 'basics',
    core: true,
    label: 'flags.shell.label',
    description: 'flags.shell.descr'
  },
  {
    key: 'home',
    group: 'basics',
    core: true,
    label: 'flags.home.label',
    description: 'flags.home.descr'
  },
  {
    key: 'search',
    group: 'basics',
    core: true,
    label: 'flags.search.label',
    description: 'flags.search.descr'
  },
  {
    key: 'people',
    group: 'basics',
    core: true,
    label: 'flags.people.label',
    description: 'flags.people.descr'
  },
  {
    key: 'profile',
    group: 'basics',
    core: true,
    label: 'flags.profile.label',
    description: 'flags.profile.descr'
  },
  {
    key: 'notifications',
    group: 'basics',
    core: true,
    label: 'flags.notifications.label',
    description: 'flags.notifications.descr'
  },
  {
    key: 'theme',
    group: 'basics',
    core: true,
    label: 'flags.theme.label',
    description: 'flags.theme.descr'
  },
  {
    key: 'multiWorkspace',
    group: 'basics',
    core: true,
    label: 'flags.multiWorkspace.label',
    description: 'flags.multiWorkspace.descr'
  },

  // ── Canvas views
  {
    key: 'studio',
    group: 'views',
    label: 'flags.studio.label',
    description: 'flags.studio.descr'
  },
  {
    key: 'infra',
    group: 'views',
    label: 'flags.infra.label',
    description: 'flags.infra.descr'
  },
  {
    key: 'tasks',
    group: 'views',
    label: 'flags.tasks.label',
    description: 'flags.tasks.descr'
  },
  {
    key: 'agents',
    group: 'views',
    label: 'flags.agents.label',
    description: 'flags.agents.descr'
  },

  // ── Workspace modules
  {
    key: 'calendar',
    group: 'modules',
    label: 'flags.calendar.label',
    description: 'flags.calendar.descr'
  },
  {
    key: 'reminders',
    group: 'modules',
    label: 'flags.reminders.label',
    description: 'flags.reminders.descr'
  },
  // History — the built-in timeline of the workspace activity log. On by
  // default like the other built-in modules; gates the navbar rows, the
  // launcher tile and the /history route through ROUTE_FLAGS below.
  {
    key: 'history',
    group: 'modules',
    label: 'flags.history.label',
    description: 'flags.history.descr'
  },
  {
    key: 'chat',
    group: 'modules',
    label: 'flags.chat.label',
    description: 'flags.chat.descr'
  },
  // Dark by default — the FIRST flag in this registry with
  // `defaultOn: false`. `featureEnabled` reads `def.defaultOn !== false`, so
  // Mail stays off (header button, navbar entries and the /mail route all
  // hidden) until an admin turns it on at /flags. Every other module ships
  // on, so omitting the key would have shipped Mail live to every workspace.
  {
    key: 'mail',
    group: 'modules',
    defaultOn: false,
    label: 'flags.mail.label',
    description: 'flags.mail.descr'
  },
  {
    key: 'meet',
    group: 'modules',
    label: 'flags.meet.label',
    description: 'flags.meet.descr'
  },
  {
    key: 'documents',
    group: 'modules',
    label: 'flags.documents.label',
    description: 'flags.documents.descr'
  },
  {
    key: 'files',
    group: 'modules',
    label: 'flags.files.label',
    description: 'flags.files.descr'
  },
  {
    key: 'resolutions',
    group: 'modules',
    label: 'flags.resolutions.label',
    description: 'flags.resolutions.descr'
  },
  {
    key: 'analytics',
    group: 'modules',
    label: 'flags.analytics.label',
    description: 'flags.analytics.descr'
  },
  {
    key: 'data',
    group: 'modules',
    label: 'flags.data.label',
    description: 'flags.data.descr'
  },
  {
    key: 'announcements',
    group: 'modules',
    label: 'flags.announcements.label',
    description: 'flags.announcements.descr'
  },
  {
    key: 'company',
    group: 'modules',
    label: 'flags.company.label',
    description: 'flags.company.descr'
  },

  // ── AI & collaboration
  {
    key: 'aiAssistant',
    group: 'ai',
    label: 'flags.aiAssistant.label',
    description: 'flags.aiAssistant.descr'
  },
  {
    key: 'realtime',
    group: 'ai',
    label: 'flags.realtime.label',
    description: 'flags.realtime.descr'
  },
  {
    key: 'splitView',
    group: 'ai',
    label: 'flags.splitView.label',
    description: 'flags.splitView.descr'
  },

  // ── Marketplace & apps
  {
    key: 'marketplace',
    group: 'apps',
    label: 'flags.marketplace.label',
    description: 'flags.marketplace.descr'
  },
  {
    key: 'communityFeed',
    group: 'apps',
    label: 'flags.communityFeed.label',
    description: 'flags.communityFeed.descr'
  },
  {
    key: 'promptGallery',
    group: 'apps',
    label: 'flags.promptGallery.label',
    description: 'flags.promptGallery.descr'
  },
  {
    key: 'workspaceApps',
    group: 'apps',
    label: 'flags.workspaceApps.label',
    description: 'flags.workspaceApps.descr'
  },
  {
    key: 'customApps',
    group: 'apps',
    label: 'flags.customApps.label',
    description: 'flags.customApps.descr'
  },
  // AI-written apps run in an opaque sandbox frame instead of the shell
  // (ISOLATED-RUNTIME-PLAN §8, workspace functions/generatedAppSandbox.js).
  // OFF by default until the 2026-10-10 review; a TOP-LEVEL flag on purpose:
  // a security switch must not turn off with an unrelated parent.
  {
    key: 'appsSandbox',
    group: 'apps',
    defaultOn: false,
    label: 'flags.appsSandbox.label',
    description: 'flags.appsSandbox.descr'
  }
]

// ── Sub-features — the "detail down" per feature (2026-07-25 per Nika
// "add sub features per it, toggleable per feature … for example tickets:
// my view, standups"). Keyed by parent flag; each sub stores under the
// dotted key `<parent>.<sub>` in the same overrides object. A sub is
// effective only while its parent is on (featureEnabled gates through
// parentKey); subs of CORE parents stay user-toggleable — that's the
// point of locking the parent but tuning its details.
export const FEATURE_SUBFLAGS = {
  shell: [
    {
      key: 'keyboardShortcuts',
      label: 'flags.shellKeyboardShortcuts.label',
      description: 'flags.shellKeyboardShortcuts.descr'
    },
    {
      key: 'moduleTabs',
      label: 'flags.shellModuleTabs.label',
      description: 'flags.shellModuleTabs.descr'
    },
    {
      key: 'contextMenus',
      label: 'flags.shellContextMenus.label',
      description: 'flags.shellContextMenus.descr'
    }
  ],
  home: [
    {
      key: 'widgets',
      label: 'flags.homeWidgets.label',
      description: 'flags.homeWidgets.descr'
    },
    {
      key: 'appIcons',
      label: 'flags.homeAppIcons.label',
      description: 'flags.homeAppIcons.descr'
    },
    {
      key: 'agentsWidget',
      label: 'flags.homeAgentsWidget.label',
      description: 'flags.homeAgentsWidget.descr'
    },
    {
      key: 'analyticsWidget',
      label: 'flags.homeAnalyticsWidget.label',
      description: 'flags.homeAnalyticsWidget.descr'
    },
    {
      key: 'eventsWidget',
      label: 'flags.homeEventsWidget.label',
      description: 'flags.homeEventsWidget.descr'
    },
    {
      key: 'chatRail',
      label: 'flags.homeChatRail.label',
      description: 'flags.homeChatRail.descr'
    }
  ],
  search: [
    {
      key: 'commandSearch',
      label: 'flags.searchCommandSearch.label',
      description: 'flags.searchCommandSearch.descr'
    },
    {
      key: 'appsLauncher',
      label: 'flags.searchAppsLauncher.label',
      description: 'flags.searchAppsLauncher.descr'
    },
    {
      key: 'headerMorph',
      label: 'flags.searchHeaderMorph.label',
      description: 'flags.searchHeaderMorph.descr'
    }
  ],
  people: [
    {
      key: 'memberProfiles',
      label: 'flags.peopleMemberProfiles.label',
      description: 'flags.peopleMemberProfiles.descr'
    },
    {
      key: 'roleManagement',
      label: 'flags.peopleRoleManagement.label',
      description: 'flags.peopleRoleManagement.descr'
    },
    {
      key: 'invites',
      label: 'flags.peopleInvites.label',
      description: 'flags.peopleInvites.descr'
    }
  ],
  notifications: [
    {
      key: 'feed',
      label: 'flags.notificationsFeed.label',
      description: 'flags.notificationsFeed.descr'
    },
    {
      key: 'unreadBadges',
      label: 'flags.notificationsUnreadBadges.label',
      description: 'flags.notificationsUnreadBadges.descr'
    }
  ],
  theme: [
    {
      key: 'systemSync',
      label: 'flags.themeSystemSync.label',
      description: 'flags.themeSystemSync.descr'
    },
    {
      key: 'workspaceSkins',
      label: 'flags.themeWorkspaceSkins.label',
      description: 'flags.themeWorkspaceSkins.descr'
    }
  ],
  multiWorkspace: [
    {
      key: 'workspaceUrls',
      label: 'flags.multiWorkspaceWorkspaceUrls.label',
      description: 'flags.multiWorkspaceWorkspaceUrls.descr'
    },
    {
      key: 'orgSwitcher',
      label: 'flags.multiWorkspaceOrgSwitcher.label',
      description: 'flags.multiWorkspaceOrgSwitcher.descr'
    }
  ],
  studio: [
    {
      key: 'studiosBoards',
      label: 'flags.studioStudiosBoards.label',
      description: 'flags.studioStudiosBoards.descr'
    },
    {
      key: 'projectThumbnails',
      label: 'flags.studioProjectThumbnails.label',
      description: 'flags.studioProjectThumbnails.descr'
    },
    {
      key: 'canvasSidebars',
      label: 'flags.studioCanvasSidebars.label',
      description: 'flags.studioCanvasSidebars.descr'
    },
    {
      key: 'projectCreation',
      label: 'flags.studioProjectCreation.label',
      description: 'flags.studioProjectCreation.descr'
    }
  ],
  infra: [
    {
      key: 'statusPage',
      label: 'flags.infraStatusPage.label',
      description: 'flags.infraStatusPage.descr'
    },
    {
      key: 'servicesList',
      label: 'flags.infraServicesList.label',
      description: 'flags.infraServicesList.descr'
    },
    {
      key: 'environments',
      label: 'flags.infraEnvironments.label',
      description: 'flags.infraEnvironments.descr'
    }
  ],
  tasks: [
    {
      key: 'myView',
      label: 'flags.tasksMyView.label',
      description: 'flags.tasksMyView.descr'
    },
    {
      key: 'standups',
      label: 'flags.tasksStandups.label',
      description: 'flags.tasksStandups.descr'
    },
    { key: 'feed', label: 'flags.tasksFeed.label', description: 'flags.tasksFeed.descr' },
    {
      key: 'features',
      label: 'flags.tasksFeatures.label',
      description: 'flags.tasksFeatures.descr'
    },
    {
      key: 'planning',
      label: 'flags.tasksPlanning.label',
      description: 'flags.tasksPlanning.descr'
    },
    {
      key: 'typeFilters',
      label: 'flags.tasksTypeFilters.label',
      description: 'flags.tasksTypeFilters.descr'
    },
    {
      key: 'generalTriage',
      label: 'flags.tasksGeneralTriage.label',
      description: 'flags.tasksGeneralTriage.descr'
    },
    {
      key: 'githubSync',
      label: 'flags.tasksGithubSync.label',
      description: 'flags.tasksGithubSync.descr'
    },
    {
      key: 'teamAgentLenses',
      label: 'flags.tasksTeamAgentLenses.label',
      description: 'flags.tasksTeamAgentLenses.descr'
    }
  ],
  agents: [
    {
      key: 'myAgents',
      label: 'flags.agentsMyAgents.label',
      description: 'flags.agentsMyAgents.descr'
    },
    {
      key: 'assignments',
      label: 'flags.agentsAssignments.label',
      description: 'flags.agentsAssignments.descr'
    },
    {
      key: 'permissions',
      label: 'flags.agentsPermissions.label',
      description: 'flags.agentsPermissions.descr'
    },
    { key: 'tools', label: 'flags.agentsTools.label', description: 'flags.agentsTools.descr' }
  ],
  calendar: [
    {
      key: 'miniCalendar',
      label: 'flags.calendarMiniCalendar.label',
      description: 'flags.calendarMiniCalendar.descr'
    },
    {
      key: 'monthWeekViews',
      label: 'flags.calendarMonthWeekViews.label',
      description: 'flags.calendarMonthWeekViews.descr'
    },
    {
      key: 'teamView',
      label: 'flags.calendarTeamView.label',
      description: 'flags.calendarTeamView.descr'
    },
    {
      key: 'availability',
      label: 'flags.calendarAvailability.label',
      description: 'flags.calendarAvailability.descr'
    },
    {
      key: 'bookings',
      label: 'flags.calendarBookings.label',
      description: 'flags.calendarBookings.descr'
    },
    {
      key: 'reminders',
      label: 'flags.calendarReminders.label',
      description: 'flags.calendarReminders.descr'
    },
    {
      key: 'linkedResources',
      label: 'flags.calendarLinkedResources.label',
      description: 'flags.calendarLinkedResources.descr'
    }
  ],
  chat: [
    { key: 'channels', label: 'flags.chatChannels.label', description: 'flags.chatChannels.descr' },
    {
      key: 'directMessages',
      label: 'flags.chatDirectMessages.label',
      description: 'flags.chatDirectMessages.descr'
    },
    { key: 'threads', label: 'flags.chatThreads.label', description: 'flags.chatThreads.descr' },
    {
      key: 'messengerOverlay',
      label: 'flags.chatMessengerOverlay.label',
      description: 'flags.chatMessengerOverlay.descr'
    },
    {
      key: 'chatDrawer',
      label: 'flags.chatChatDrawer.label',
      description: 'flags.chatChatDrawer.descr'
    }
  ],
  meet: [
    {
      key: 'transcripts',
      label: 'flags.meetTranscripts.label',
      description: 'flags.meetTranscripts.descr'
    },
    {
      key: 'liveCaptions',
      label: 'flags.meetLiveCaptions.label',
      description: 'flags.meetLiveCaptions.descr'
    },
    { key: 'rooms', label: 'flags.meetRooms.label', description: 'flags.meetRooms.descr' },
    {
      key: 'guestLinks',
      label: 'flags.meetGuestLinks.label',
      description: 'flags.meetGuestLinks.descr'
    }
  ],
  documents: [
    {
      key: 'knowledgeBase',
      label: 'flags.documentsKnowledgeBase.label',
      description: 'flags.documentsKnowledgeBase.descr'
    },
    {
      key: 'notes',
      label: 'flags.documentsNotes.label',
      description: 'flags.documentsNotes.descr'
    },
    {
      key: 'editor',
      label: 'flags.documentsEditor.label',
      description: 'flags.documentsEditor.descr'
    },
    {
      key: 'collabEditing',
      label: 'flags.documentsCollabEditing.label',
      description: 'flags.documentsCollabEditing.descr'
    },
    {
      key: 'exportTools',
      label: 'flags.documentsExportTools.label',
      description: 'flags.documentsExportTools.descr'
    }
  ],
  files: [
    {
      key: 'folders',
      label: 'flags.filesFolders.label',
      description: 'flags.filesFolders.descr'
    },
    {
      key: 'desktopGrid',
      label: 'flags.filesDesktopGrid.label',
      description: 'flags.filesDesktopGrid.descr'
    }
  ],
  resolutions: [
    {
      key: 'requests',
      label: 'flags.resolutionsRequests.label',
      description: 'flags.resolutionsRequests.descr'
    },
    {
      key: 'improvements',
      label: 'flags.resolutionsImprovements.label',
      description: 'flags.resolutionsImprovements.descr'
    },
    {
      key: 'editRequests',
      label: 'flags.resolutionsEditRequests.label',
      description: 'flags.resolutionsEditRequests.descr'
    },
    {
      key: 'audit',
      label: 'flags.resolutionsAudit.label',
      description: 'flags.resolutionsAudit.descr'
    }
  ],
  analytics: [
    {
      key: 'projectAnalytics',
      label: 'flags.analyticsProjectAnalytics.label',
      description: 'flags.analyticsProjectAnalytics.descr'
    },
    {
      key: 'workspaceUsage',
      label: 'flags.analyticsWorkspaceUsage.label',
      description: 'flags.analyticsWorkspaceUsage.descr'
    },
    {
      key: 'liveMap',
      label: 'flags.analyticsLiveMap.label',
      description: 'flags.analyticsLiveMap.descr'
    },
    {
      key: 'logs',
      label: 'flags.analyticsLogs.label',
      description: 'flags.analyticsLogs.descr'
    },
    {
      key: 'bugsFeed',
      label: 'flags.analyticsBugsFeed.label',
      description: 'flags.analyticsBugsFeed.descr'
    },
    {
      key: 'sessionsExplorer',
      label: 'flags.analyticsSessionsExplorer.label',
      description: 'flags.analyticsSessionsExplorer.descr'
    }
  ],
  data: [
    { key: 'users', label: 'flags.dataUsers.label', description: 'flags.dataUsers.descr' },
    {
      key: 'organizations',
      label: 'flags.dataOrganizations.label',
      description: 'flags.dataOrganizations.descr'
    },
    { key: 'projects', label: 'flags.dataProjects.label', description: 'flags.dataProjects.descr' },
    {
      key: 'plans',
      label: 'flags.dataPlans.label',
      description: 'flags.dataPlans.descr'
    },
    { key: 'teams', label: 'flags.dataTeams.label', description: 'flags.dataTeams.descr' },
    {
      key: 'externalSources',
      label: 'flags.dataExternalSources.label',
      description: 'flags.dataExternalSources.descr'
    }
  ],
  announcements: [
    {
      key: 'polls',
      label: 'flags.announcementsPolls.label',
      description: 'flags.announcementsPolls.descr'
    },
    {
      key: 'tagFilters',
      label: 'flags.announcementsTagFilters.label',
      description: 'flags.announcementsTagFilters.descr'
    }
  ],
  company: [
    {
      key: 'profileEditing',
      label: 'flags.companyProfileEditing.label',
      description: 'flags.companyProfileEditing.descr'
    }
  ],
  aiAssistant: [
    {
      key: 'composer',
      label: 'flags.aiAssistantComposer.label',
      description: 'flags.aiAssistantComposer.descr'
    },
    {
      key: 'answerViews',
      label: 'flags.aiAssistantAnswerViews.label',
      description: 'flags.aiAssistantAnswerViews.descr'
    },
    {
      key: 'buildApps',
      label: 'flags.aiAssistantBuildApps.label',
      description: 'flags.aiAssistantBuildApps.descr'
    },
    {
      key: 'pageAwareness',
      label: 'flags.aiAssistantPageAwareness.label',
      description: 'flags.aiAssistantPageAwareness.descr'
    },
    {
      key: 'reasoningTimeline',
      label: 'flags.aiAssistantReasoningTimeline.label',
      description: 'flags.aiAssistantReasoningTimeline.descr'
    },
    {
      key: 'suggestionChips',
      label: 'flags.aiAssistantSuggestionChips.label',
      description: 'flags.aiAssistantSuggestionChips.descr'
    },
    {
      key: 'moduleAi',
      label: 'flags.aiAssistantModuleAi.label',
      description: 'flags.aiAssistantModuleAi.descr'
    }
  ],
  realtime: [
    {
      key: 'presence',
      label: 'flags.realtimePresence.label',
      description: 'flags.realtimePresence.descr'
    },
    {
      key: 'onlineDock',
      label: 'flags.realtimeOnlineDock.label',
      description: 'flags.realtimeOnlineDock.descr'
    },
    {
      key: 'collabSessions',
      label: 'flags.realtimeCollabSessions.label',
      description: 'flags.realtimeCollabSessions.descr'
    }
  ],
  splitView: [
    {
      key: 'appWindows',
      label: 'flags.splitViewAppWindows.label',
      description: 'flags.splitViewAppWindows.descr'
    },
    {
      key: 'splitPane',
      label: 'flags.splitViewSplitPane.label',
      description: 'flags.splitViewSplitPane.descr'
    },
    {
      key: 'fullscreenMode',
      label: 'flags.splitViewFullscreenMode.label',
      description: 'flags.splitViewFullscreenMode.descr'
    },
    {
      key: 'pageRestore',
      label: 'flags.splitViewPageRestore.label',
      description: 'flags.splitViewPageRestore.descr'
    }
  ],
  marketplace: [
    {
      key: 'featured',
      label: 'flags.marketplaceFeatured.label',
      description: 'flags.marketplaceFeatured.descr'
    },
    {
      key: 'detailGallery',
      label: 'flags.marketplaceDetailGallery.label',
      description: 'flags.marketplaceDetailGallery.descr'
    },
    {
      key: 'livePreviews',
      label: 'flags.marketplaceLivePreviews.label',
      description: 'flags.marketplaceLivePreviews.descr'
    }
  ],
  communityFeed: [
    {
      key: 'comments',
      label: 'flags.communityFeedComments.label',
      description: 'flags.communityFeedComments.descr'
    },
    {
      key: 'follows',
      label: 'flags.communityFeedFollows.label',
      description: 'flags.communityFeedFollows.descr'
    },
    {
      key: 'shareComposer',
      label: 'flags.communityFeedShareComposer.label',
      description: 'flags.communityFeedShareComposer.descr'
    }
  ],
  promptGallery: [
    {
      key: 'copyActions',
      label: 'flags.promptGalleryCopyActions.label',
      description: 'flags.promptGalleryCopyActions.descr'
    }
  ],
  workspaceApps: [
    {
      key: 'crm',
      label: 'flags.workspaceAppsCrm.label',
      description: 'flags.workspaceAppsCrm.descr'
    },
    { key: 'hr', label: 'flags.workspaceAppsHr.label', description: 'flags.workspaceAppsHr.descr' },
    {
      key: 'commerce',
      label: 'flags.workspaceAppsCommerce.label',
      description: 'flags.workspaceAppsCommerce.descr'
    },
    {
      key: 'scheduling',
      label: 'flags.workspaceAppsScheduling.label',
      description: 'flags.workspaceAppsScheduling.descr'
    },
    {
      key: 'cms',
      label: 'flags.workspaceAppsCms.label',
      description: 'flags.workspaceAppsCms.descr'
    },
    {
      key: 'financials',
      label: 'flags.workspaceAppsFinancials.label',
      description: 'flags.workspaceAppsFinancials.descr'
    }
  ],
  customApps: [
    {
      key: 'aiBuilt',
      label: 'flags.customAppsAiBuilt.label',
      description: 'flags.customAppsAiBuilt.descr'
    },
    {
      key: 'sharing',
      label: 'flags.customAppsSharing.label',
      description: 'flags.customAppsSharing.descr'
    }
  ]
}

// Public registry — the raw entries with their sub-features attached.
// Pure module-init-time derivation, not a function body — unaffected by
// the frank trap (see file header).
export const FEATURE_FLAGS = RAW_FEATURE_FLAGS.map((f) =>
  FEATURE_SUBFLAGS[f.key] ? { ...f, subs: FEATURE_SUBFLAGS[f.key] } : f
)

// Flat lookup over parents AND dotted sub-keys (`tasks.standups`), built
// once at module-init time — sub entries carry `parentKey` so featureEnabled
// can gate through the parent. Also pure module-init-time code, not a
// function body.
function buildFlagIndex() {
  const map = {}
  FEATURE_FLAGS.forEach((f) => {
    map[f.key] = f
    ;(f.subs || []).forEach((sub) => {
      map[`${f.key}.${sub.key}`] = { ...sub, key: `${f.key}.${sub.key}`, parentKey: f.key }
    })
  })
  return map
}
export const FLAG_INDEX = buildFlagIndex()

// ── Navigation gating ────────────────────────────────────────────────────
// Route-prefix → flag map: the single brain behind `navItemVisible`, so
// navigation registries (AppNavbar APPS/DROPDOWN_APPS, HEADER_TABS,
// VIEW_NAV, launcher tiles, workspace-config navbar entries) don't each
// hand-carry a flag field. An item may still declare an explicit
// `flag: 'chat'` (or `flag: false` to opt out of gating) — the property
// wins over the route lookup. (2026-07-25 per Nika "add if: as property
// for all navigation items and conditional rendering".)
export const ROUTE_FLAGS = [
  ['/studio', 'studio'],
  ['/canvas', 'studio'],
  // The Workspace apps view of the Apps hub (docs/APP_SPLIT.md) — the same
  // product surface as /studio, so the same flag.
  ['/apps', 'studio'],
  ['/infra', 'infra'],
  // /tasks is the canonical route (tickets→tasks rename 2026-08-06 per
  // Nika); the /tickets* forms stay as inbound legacy aliases.
  ['/tasks-page', 'tasks'],
  ['/tasks', 'tasks'],
  ['/task', 'tasks'],
  ['/tickets-page', 'tasks'],
  ['/tickets', 'tasks'],
  ['/ticket', 'tasks'],
  ['/team-and-agents', 'agents'],
  ['/my-agents', 'agents'],
  ['/agents', 'agents'],
  ['/calendar', 'calendar'],
  ['/reminders', 'reminders'],
  ['/history', 'history'],
  ['/chat', 'chat'],
  ['/mail', 'mail'],
  ['/meet', 'meet'],
  // /notes is the canonical route (documents→notes rename 2026-08-06); the
  // flag key stays the internal 'documents'. '/documents' = legacy alias.
  ['/notes', 'documents'],
  ['/documents', 'documents'],
  ['/files', 'files'],
  ['/resolutions', 'resolutions'],
  ['/workspace-analytics', 'analytics'],
  ['/analytics', 'analytics'],
  ['/logs', 'analytics'],
  ['/data', 'data'],
  ['/announcements', 'announcements'],
  ['/company', 'company'],
  ['/marketplace/prompt-gallery', 'promptGallery'],
  ['/marketplace/feed', 'communityFeed'],
  ['/marketplace', 'marketplace'],
  ['/feed', 'communityFeed'],
  ['/add-app', 'customApps'],
  // Installed workspace-app routes (system catalog apps).
  ['/crm', 'workspaceApps.crm'],
  ['/contacts', 'workspaceApps.crm'],
  ['/hr', 'workspaceApps.hr'],
  ['/commerce', 'workspaceApps.commerce'],
  ['/scheduling', 'workspaceApps.scheduling'],
  ['/cms', 'workspaceApps.cms'],
  ['/financials', 'workspaceApps.financials']
]
